class AevloBundleSelector extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.hero = this.closest('[data-aevlo-product-hero]');
    this.unitsContainer = this.hero?.querySelector('[data-aevlo-bundle-units]');
    if (!this.hero || !this.unitsContainer) return;
    this.variants = JSON.parse(this.querySelector('[data-bundle-variants]').textContent);
    this.nativeVariantId = this.hero?.querySelector('product-form-component input[name="id"]')?.value;
    this.moneyFormatter = new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: this.dataset.currency,
    });
    this.handleVariantUpdate = (event) => {
      if (String(event.detail.data.productId) !== this.dataset.productId) return;
      this.nativeVariantId = event.detail.resource?.id ?? null;
      this.renderPrices();
    };
    this.hero?.addEventListener('variant:update', this.handleVariantUpdate);
    this.unitsContainer.querySelectorAll('[data-bundle-unit]').forEach((unit) => {
      const styleSelect = unit.querySelector('[data-bundle-style]');
      styleSelect.add(new Option('Select style', ''));
      [...new Set(this.variants.filter((variant) => variant.available).map((variant) => variant.style))].forEach((style) => {
        styleSelect.add(new Option(style, style));
      });
      styleSelect.addEventListener('change', () => this.updateSizes(unit));
      unit.querySelector('[data-bundle-size]').addEventListener('change', () => {
        this.clearError();
        this.markIncompleteUnits();
        this.renderPrices();
      });
      this.updateSizes(unit);
    });
    this.addEventListener('pointerdown', (event) => {
      this.pointerOffer = event.target.closest('[data-bundle-offer]')?.querySelector('input[type="radio"]');
    });
    this.addEventListener('change', (event) => {
      if (event.target.matches('input[type="radio"]')) {
        this.updateQuantity();
        if (this.pointerOffer === event.target) this.guideToSelection();
        this.pointerOffer = null;
      }
    });
    this.updateQuantity();
    this.hidden = false;
    this.dataset.ready = 'true';
    this.hero?.classList.add('aevlo-bundle-ready');
  }

  disconnectedCallback() {
    this.hero?.removeEventListener('variant:update', this.handleVariantUpdate);
  }

  get quantity() {
    return Number(this.querySelector('input[type="radio"]:checked')?.value || 1);
  }

  updateSizes(unit) {
    const style = unit.querySelector('[data-bundle-style]').value;
    const sizeSelect = unit.querySelector('[data-bundle-size]');
    const previous = sizeSelect.value;
    sizeSelect.replaceChildren();
    sizeSelect.add(new Option('Select size', ''));
    this.variants.filter((variant) => variant.style === style && variant.available).forEach((variant) => {
      sizeSelect.add(new Option(variant.size, variant.size));
    });
    if (previous && [...sizeSelect.options].some((option) => option.value === previous)) sizeSelect.value = previous;
    this.clearError();
    this.markIncompleteUnits();
    this.renderPrices();
  }

  updateQuantity() {
    const quantity = this.quantity;
    this.unitsContainer.hidden = quantity === 1;
    this.unitsContainer.querySelectorAll('[data-bundle-unit]').forEach((unit) => {
      unit.hidden = Number(unit.dataset.bundleUnit) > quantity;
    });
    this.hero?.classList.toggle('aevlo-bundle-multiple', quantity > 1);
    const nativeQuantity = this.hero?.querySelector('product-form-component input[name="quantity"]');
    if (nativeQuantity) {
      nativeQuantity.value = 1;
      nativeQuantity.dispatchEvent(new Event('change', { bubbles: true }));
    }
    this.clearError();
    this.markIncompleteUnits();
    this.renderPrices();
  }

  unitVariant(unit) {
    const style = unit.querySelector('[data-bundle-style]').value;
    const size = unit.querySelector('[data-bundle-size]').value;
    return this.variants.find((variant) => variant.style === style && variant.size === size && variant.available);
  }

  markIncompleteUnits() {
    this.unitsContainer.querySelectorAll('[data-bundle-unit]').forEach((unit) => {
      unit.classList.toggle('is-incomplete', !unit.hidden && !this.unitVariant(unit));
    });
  }

  guideToSelection() {
    let target;
    const first = this.variants.find((variant) => String(variant.id) === String(this.nativeVariantId) && variant.available);
    if (!first) {
      const picker = this.hero.querySelector('variant-picker');
      const incomplete = [...(picker?.querySelectorAll('fieldset.variant-option') || [])].find((field) => !field.querySelector('input:checked'));
      target = incomplete?.querySelector('input:not([aria-disabled="true"])') || picker?.querySelector('input:not([aria-disabled="true"]), select');
    } else {
      const incomplete = [...this.unitsContainer.querySelectorAll('[data-bundle-unit]')].find((unit) => !unit.hidden && !this.unitVariant(unit));
      target = incomplete && (incomplete.querySelector('[data-bundle-style]').value ? incomplete.querySelector('[data-bundle-size]') : incomplete.querySelector('[data-bundle-style]'));
    }
    if (!target) return;
    target.focus({ preventScroll: true });
    target.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
  }

  selectedVariants(quantity) {
    const first = this.variants.find((variant) => String(variant.id) === String(this.nativeVariantId) && variant.available);
    if (!first) return null;
    const selected = [first];
    for (const unit of this.unitsContainer.querySelectorAll('[data-bundle-unit]')) {
      if (Number(unit.dataset.bundleUnit) > quantity) continue;
      const variant = this.unitVariant(unit);
      if (!variant) return null;
      selected.push(variant);
    }
    return selected;
  }

  priceVariants(quantity) {
    const available = this.variants.filter((variant) => variant.available && Number.isInteger(Number(variant.price)));
    if (!available.length) return null;
    const lowest = (variants) => variants.reduce((best, variant) => Number(variant.price) < Number(best.price) ? variant : best);
    const first = available.find((variant) => String(variant.id) === String(this.nativeVariantId));
    const selected = [first || lowest(available)];
    let from = !first;
    for (const unit of this.unitsContainer.querySelectorAll('[data-bundle-unit]')) {
      if (Number(unit.dataset.bundleUnit) > quantity) continue;
      const exact = this.unitVariant(unit);
      if (exact) {
        selected.push(exact);
      } else {
        const style = unit.querySelector('[data-bundle-style]').value;
        const possible = available.filter((variant) => !style || variant.style === style);
        if (!possible.length) return null;
        selected.push(lowest(possible));
        from = true;
      }
    }
    return { variants: selected, from };
  }

  formatPrice(cents) {
    const template = this.dataset?.moneyFormat;
    if (!template) return this.moneyFormatter.format(cents / 100);
    return template.replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
      if (placeholder === 'currency') return this.dataset.currency;
      let precision = this.moneyFormatter.resolvedOptions().maximumFractionDigits;
      let thousands = ',';
      let decimal = '.';
      if (placeholder.includes('no_decimals')) precision = 0;
      if (placeholder.includes('comma_separator')) { thousands = '.'; decimal = ','; }
      if (placeholder.includes('space_separator')) { thousands = ' '; decimal = placeholder.includes('period_and_space') ? '.' : ','; }
      if (placeholder.includes('apostrophe_separator')) thousands = "'";
      const [whole, fraction] = (cents / 100).toFixed(precision).split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fraction ? `${grouped}${decimal}${fraction}` : grouped;
    });
  }

  renderPrices() {
    if (!this.moneyFormatter) return;
    this.querySelectorAll('[data-bundle-offer]').forEach((offer) => {
      const quantity = Number(offer.querySelector('input[type="radio"]').value);
      const quote = this.priceVariants(quantity);
      const total = offer.querySelector('[data-bundle-total]');
      const original = offer.querySelector('[data-bundle-original]');
      if (!quote) {
        total.textContent = '';
        original.textContent = '';
        original.hidden = true;
        return;
      }
      const subtotal = quote.variants.reduce((sum, variant) => sum + Number(variant.price), 0);
      const discount = Math.min(100, Math.max(0, Number(offer.dataset.discountPercent) || 0));
      const discounted = Math.round(subtotal * (100 - discount) / 100);
      total.textContent = `${quote.from ? 'From ' : ''}${this.formatPrice(discounted)}`;
      original.hidden = quote.from || discount === 0 || discounted >= subtotal;
      original.textContent = original.hidden ? '' : this.formatPrice(subtotal);
    });
  }

  clearError() {
    const error = this.querySelector('[data-bundle-error]');
    error.hidden = true;
    error.textContent = '';
  }

  getCartItems(nativeVariantId) {
    if (!this.variants.some((variant) => String(variant.id) === String(nativeVariantId) && variant.available)) return null;
    const items = [{ id: Number(nativeVariantId), quantity: 1 }];
    for (const unit of this.unitsContainer.querySelectorAll('[data-bundle-unit]')) {
      if (Number(unit.dataset.bundleUnit) > this.quantity) continue;
      const variant = this.unitVariant(unit);
      if (!variant) {
        const error = this.querySelector('[data-bundle-error]');
        error.textContent = `Choose an available style and size for unit ${unit.dataset.bundleUnit}.`;
        error.hidden = false;
        return null;
      }
      const existing = items.find((item) => item.id === Number(variant.id));
      if (existing) existing.quantity += 1;
      else items.push({ id: Number(variant.id), quantity: 1 });
    }
    this.clearError();
    return items;
  }
}

if (!customElements.get('aevlo-bundle-selector')) {
  customElements.define('aevlo-bundle-selector', AevloBundleSelector);
}
